/**
 * Project Overview metrics: load-once payloads with client-side Type / Range filters.
 */

import { formatCountLabel } from "../../../../utils/common/formatting/labels";
import { formatNumber } from "../../../../utils/common/formatting/numbers";
import {
  countDistinctDaysByRangeKey,
  formatShortMonthDay,
  getDateInLocalTZFromDateStr,
  getMonthDateRange,
  isDateInInclusiveRange,
  toLocalDateKey,
  today,
} from "../../../../utils/datetime";

/** Sentinel selection key for project-lifetime metrics / All type filters. */
export const OVERVIEW_METRICS_RANGE_ALL = "all";
export const OVERVIEW_METRICS_TYPE_ALL = "all";

/** Concrete Pours Type dropdown base options (labels get counts appended). */
export const CONCRETE_POURS_TYPE_OPTIONS = [
  { value: "all", label: "All" },
  { value: "past", label: "Past Only" },
  { value: "dispatched", label: "Dispatched Only" },
  { value: "reviewed", label: "Reviewed Only" },
];

/**
 * Default metrics selection: entire project (no start/end query params).
 *
 * @returns {{key: string, start: null, end: null}}
 */
export function getDefaultOverviewMetricsSelection() {
  return {
    key: OVERVIEW_METRICS_RANGE_ALL,
    start: null,
    end: null,
  };
}

/**
 * Resolve a dropdown value into a metrics selection object.
 *
 * @param {string} key - ``all`` or ``YYYY-MM``.
 * @returns {{key: string, start: string|null, end: string|null}}
 */
export function selectionFromRangeKey(key) {
  if (!key || key === OVERVIEW_METRICS_RANGE_ALL) {
    return getDefaultOverviewMetricsSelection();
  }
  const { start, end } = getMonthDateRange(key);
  return { key, start, end };
}

/**
 * Round numeric metric values to two decimal places (matches backend helpers).
 *
 * @param {number} value
 * @returns {number}
 */
function roundMetric(value) {
  return Math.round((Number(value) || 0) * 100) / 100;
}

/**
 * Whether an ISO date key is inside the metrics range selection (All = no bounds).
 *
 * @param {string} dateKey - YYYY-MM-DD.
 * @param {{start: string|null, end: string|null}|null} range
 * @returns {boolean}
 */
function isMetricsDateInRange(dateKey, range) {
  if (!range?.start || !range?.end) return true;
  return isDateInInclusiveRange(
    getDateInLocalTZFromDateStr(dateKey),
    getDateInLocalTZFromDateStr(range.start),
    getDateInLocalTZFromDateStr(range.end)
  );
}

/**
 * Count unique workers per union craft within the selected Range.
 *
 * @param {Array<Object>|null|undefined} entries - Raw payout-hour entries.
 * @param {{start: string|null, end: string|null}|null} range
 * @returns {Object<string, number>} Keys ``all`` and WorkerType id strings.
 */
export function countWorkerHoursByType(entries, range = null) {
  const workersByType = { [OVERVIEW_METRICS_TYPE_ALL]: new Set() };

  (entries || []).forEach((entry) => {
    if (!entry?.date || !isMetricsDateInRange(entry.date, range)) return;
    if (entry.workerId == null) return;

    workersByType[OVERVIEW_METRICS_TYPE_ALL].add(entry.workerId);
    if (entry.workerTypeId == null) return;

    const typeKey = String(entry.workerTypeId);
    if (!workersByType[typeKey]) {
      workersByType[typeKey] = new Set();
    }
    workersByType[typeKey].add(entry.workerId);
  });

  const counts = {};
  Object.keys(workersByType).forEach((key) => {
    counts[key] = workersByType[key].size;
  });
  return counts;
}

/**
 * Count concrete pours per Type filter within the selected Range.
 *
 * @param {Array<Object>|null|undefined} entries - Raw concrete pour entries.
 * @param {{start: string|null, end: string|null}|null} range
 * @returns {{all: number, past: number, dispatched: number, reviewed: number}}
 */
export function countConcretePoursByType(entries, range = null) {
  const todayKey = toLocalDateKey(today());
  const counts = {
    all: 0,
    past: 0,
    dispatched: 0,
    reviewed: 0,
  };

  (entries || []).forEach((entry) => {
    if (!entry?.date || !isMetricsDateInRange(entry.date, range)) return;

    counts.all += 1;
    if (entry.date < todayKey) {
      counts.past += 1;
    }
    if (entry.dispatchStage && entry.dispatchStage !== "Draft") {
      counts.dispatched += 1;
    }
    if (entry.dispatchStage === "Reviewed") {
      counts.reviewed += 1;
    }
  });

  return counts;
}

/**
 * Build Worker Hours Type dropdown options with counts for the current Range.
 *
 * @param {Array<{id: number, type: string}>|null|undefined} workerTypes
 * @param {Array<Object>|null|undefined} entries - Raw payout-hour entries.
 * @param {{start: string|null, end: string|null}|null} range
 * @returns {Array<{value: string, label: string}>}
 */
export function buildWorkerHoursTypeOptions(
  workerTypes,
  entries = null,
  range = null
) {
  const counts = countWorkerHoursByType(entries, range);
  const options = [
    {
      value: OVERVIEW_METRICS_TYPE_ALL,
      label: "All",
    },
  ];
  (workerTypes || []).forEach((workerType) => {
    if (workerType?.id == null) return;
    const typeKey = String(workerType.id);
    const name = workerType.type || `Type ${workerType.id}`;
    options.push({
      value: typeKey,
      label: `${name} (${counts[typeKey] || 0})`,
    });
  });
  return options;
}

/**
 * Build Concrete Pours Type dropdown options with counts for the current Range.
 *
 * @param {Array<Object>|null|undefined} entries - Raw concrete pour entries.
 * @param {{start: string|null, end: string|null}|null} range
 * @returns {Array<{value: string, label: string}>}
 */
export function buildConcretePoursTypeOptions(entries = null, range = null) {
  const counts = countConcretePoursByType(entries, range);
  return CONCRETE_POURS_TYPE_OPTIONS.map((option) => ({
    value: option.value,
    // ``All`` omits the count — the total is implied.
    label:
      option.value === OVERVIEW_METRICS_TYPE_ALL
        ? option.label
        : `${option.label} (${counts[option.value] || 0})`,
  }));
}

/**
 * Whether a worker-hours entry matches the active Type filter.
 *
 * @param {Object} entry
 * @param {string} typeKey - ``all`` or WorkerType id string.
 * @returns {boolean}
 */
function workerHoursEntryMatchesType(entry, typeKey) {
  if (!typeKey || typeKey === OVERVIEW_METRICS_TYPE_ALL) return true;
  return Number(entry?.workerTypeId) === Number(typeKey);
}

/**
 * Whether a concrete-pour entry matches the active Type filter.
 *
 * @param {Object} entry
 * @param {string} typeKey - ``all`` | ``past`` | ``dispatched`` | ``reviewed``.
 * @returns {boolean}
 */
function concretePoursEntryMatchesType(entry, typeKey) {
  if (!typeKey || typeKey === OVERVIEW_METRICS_TYPE_ALL) return true;
  const todayKey = toLocalDateKey(today());
  if (typeKey === "past") return entry?.date < todayKey;
  if (typeKey === "dispatched") return entry?.dispatchStage !== "Draft";
  if (typeKey === "reviewed") return entry?.dispatchStage === "Reviewed";
  return true;
}

/**
 * Build Range dropdown options with distinct data-day counts for the current Type.
 *
 * @param {Array<{value: string, label: string}>|null|undefined} monthOptions
 * @param {Array<Object>|null|undefined} entries
 * @param {string} [typeKey=OVERVIEW_METRICS_TYPE_ALL]
 * @param {"hours"|"concrete"} kind
 * @returns {Array<{value: string, label: string}>}
 */
export function buildOverviewMetricsRangeOptions(
  monthOptions,
  entries = null,
  typeKey = OVERVIEW_METRICS_TYPE_ALL,
  kind = "hours"
) {
  const includeEntry =
    kind === "concrete"
      ? (entry) => concretePoursEntryMatchesType(entry, typeKey)
      : (entry) => workerHoursEntryMatchesType(entry, typeKey);
  const counts = countDistinctDaysByRangeKey(entries, {
    allKey: OVERVIEW_METRICS_RANGE_ALL,
    includeEntry,
  });

  return [
    {
      value: OVERVIEW_METRICS_RANGE_ALL,
      label: "All",
    },
    ...(monthOptions || []).map((option) => ({
      value: option.value,
      label: `${option.label} (${formatCountLabel(counts[option.value] || 0, "Day")})`,
    })),
  ];
}

/**
 * Derive Worker Hours Overview dropdown options + type label from a summary.
 *
 * @param {Object} summary - ``workerHoursSummary`` from ProjectPageContext.
 * @param {Array<{value: string, label: string}>|null|undefined} monthOptions
 * @returns {{typeKey: string, typeLabel: string, typeOptions: Array<Object>, rangeKey: string, rangeOptions: Array<Object>}}
 */
export function buildWorkerHoursOverviewUi(summary, monthOptions) {
  const typeKey = summary?.type ?? OVERVIEW_METRICS_TYPE_ALL;
  return {
    typeKey,
    typeLabel: getWorkerHoursTypeLabel(typeKey, summary?.workerTypes),
    typeOptions: buildWorkerHoursTypeOptions(
      summary?.workerTypes,
      summary?.entries,
      summary?.range
    ),
    rangeKey: summary?.range?.key ?? OVERVIEW_METRICS_RANGE_ALL,
    rangeOptions: buildOverviewMetricsRangeOptions(
      monthOptions,
      summary?.entries,
      typeKey,
      "hours"
    ),
  };
}

/**
 * Derive Concrete Pours Overview dropdown options + type label from a summary.
 *
 * @param {Object} summary - ``concretePoursSummary`` from ProjectPageContext.
 * @param {Array<{value: string, label: string}>|null|undefined} monthOptions
 * @returns {{typeKey: string, typeLabel: string, typeOptions: Array<Object>, rangeKey: string, rangeOptions: Array<Object>}}
 */
export function buildConcretePoursOverviewUi(summary, monthOptions) {
  const typeKey = summary?.type ?? OVERVIEW_METRICS_TYPE_ALL;
  return {
    typeKey,
    typeLabel: getConcretePoursTypeLabel(typeKey),
    typeOptions: buildConcretePoursTypeOptions(
      summary?.entries,
      summary?.range
    ),
    rangeKey: summary?.range?.key ?? OVERVIEW_METRICS_RANGE_ALL,
    rangeOptions: buildOverviewMetricsRangeOptions(
      monthOptions,
      summary?.entries,
      typeKey,
      "concrete"
    ),
  };
}

/**
 * Aggregate worker-hour entries for the current Type + Range selection.
 *
 * @param {Array<Object>|null|undefined} entries - Rows from getProjectPayoutHours.
 * @param {{start: string|null, end: string|null}|null} range - Date range selection.
 * @param {string} typeKey - ``all`` or WorkerType id string.
 * @returns {{totalHours: number, regularHours: number, overtimeHours: number, doubleTimeHours: number, workerCount: number, days: Array<Object>}}
 */
export function aggregateWorkerHoursEntries(
  entries,
  range = null,
  typeKey = OVERVIEW_METRICS_TYPE_ALL
) {
  const typeId =
    typeKey && typeKey !== OVERVIEW_METRICS_TYPE_ALL ? Number(typeKey) : null;

  let regularHours = 0;
  let overtimeHours = 0;
  let doubleTimeHours = 0;
  const workerIds = new Set();
  const daysByDate = {};

  (entries || []).forEach((entry) => {
    const dateKey = entry?.date;
    if (!dateKey || !isMetricsDateInRange(dateKey, range)) return;
    if (typeId != null && Number(entry.workerTypeId) !== typeId) return;

    const rt = Number(entry.regularHours) || 0;
    const ot = Number(entry.overtimeHours) || 0;
    const dt = Number(entry.doubleTimeHours) || 0;
    regularHours += rt;
    overtimeHours += ot;
    doubleTimeHours += dt;

    if (entry.workerId != null) {
      workerIds.add(entry.workerId);
    }

    if (!daysByDate[dateKey]) {
      daysByDate[dateKey] = {
        date: dateKey,
        regularHours: 0,
        overtimeHours: 0,
        doubleTimeHours: 0,
        workerIds: new Set(),
      };
    }
    daysByDate[dateKey].regularHours += rt;
    daysByDate[dateKey].overtimeHours += ot;
    daysByDate[dateKey].doubleTimeHours += dt;
    if (entry.workerId != null) {
      daysByDate[dateKey].workerIds.add(entry.workerId);
    }
  });

  const days = Object.keys(daysByDate)
    .sort()
    .map((key) => {
      const workerIds = Array.from(daysByDate[key].workerIds);
      return {
        date: daysByDate[key].date,
        regularHours: roundMetric(daysByDate[key].regularHours),
        overtimeHours: roundMetric(daysByDate[key].overtimeHours),
        doubleTimeHours: roundMetric(daysByDate[key].doubleTimeHours),
        workerCount: workerIds.length,
        // Kept for weekly chart rollups (unique workers across the week).
        workerIds,
      };
    });

  return {
    regularHours: roundMetric(regularHours),
    overtimeHours: roundMetric(overtimeHours),
    doubleTimeHours: roundMetric(doubleTimeHours),
    totalHours: roundMetric(regularHours + overtimeHours + doubleTimeHours),
    workerCount: workerIds.size,
    days,
  };
}

/** Fallback mix label when ConcreteAssignment.mix is null. */
export const CONCRETE_MIX_UNKNOWN = "No mix";

/**
 * Aggregate concrete-pour entries for the current Type + Range selection.
 *
 * @param {Array<Object>|null|undefined} entries - Rows from getProjectConcretePours.
 * @param {{start: string|null, end: string|null}|null} range - Date range selection.
 * @param {string} typeKey - ``all`` | ``past`` | ``dispatched`` | ``reviewed``.
 * @returns {{totalVolume: number, pourCount: number, averagePerPour: number, lastPourDate: string|null, days: Array<Object>, mixDesigns: string[]}}
 */
export function aggregateConcretePoursEntries(
  entries,
  range = null,
  typeKey = OVERVIEW_METRICS_TYPE_ALL
) {
  const todayKey = toLocalDateKey(today());
  let totalVolume = 0;
  let pourCount = 0;
  let lastPourDate = null;
  const daysByDate = {};
  const mixDesigns = new Set();

  (entries || []).forEach((entry) => {
    const dateKey = entry?.date;
    if (!dateKey || !isMetricsDateInRange(dateKey, range)) {
      return;
    }

    if (typeKey === "past" && dateKey >= todayKey) return;
    if (typeKey === "dispatched" && entry.dispatchStage === "Draft") return;
    if (typeKey === "reviewed" && entry.dispatchStage !== "Reviewed") return;

    const yards = Number(entry.yards) || 0;
    const mixKey = entry.designNumber || CONCRETE_MIX_UNKNOWN;
    totalVolume += yards;
    pourCount += 1;
    mixDesigns.add(mixKey);
    if (!lastPourDate || dateKey > lastPourDate) {
      lastPourDate = dateKey;
    }

    if (!daysByDate[dateKey]) {
      daysByDate[dateKey] = {
        date: dateKey,
        yards: 0,
        pourCount: 0,
        yardsByMix: {},
      };
    }
    daysByDate[dateKey].yards += yards;
    daysByDate[dateKey].pourCount += 1;
    daysByDate[dateKey].yardsByMix[mixKey] =
      (daysByDate[dateKey].yardsByMix[mixKey] || 0) + yards;
  });

  const days = Object.keys(daysByDate)
    .sort()
    .map((key) => {
      const bucket = daysByDate[key];
      const yardsByMix = {};
      Object.keys(bucket.yardsByMix).forEach((mixKey) => {
        yardsByMix[mixKey] = roundMetric(bucket.yardsByMix[mixKey]);
      });
      return {
        date: bucket.date,
        yards: roundMetric(bucket.yards),
        pourCount: bucket.pourCount,
        yardsByMix,
      };
    });

  return {
    totalVolume: roundMetric(totalVolume),
    pourCount,
    averagePerPour: pourCount > 0 ? roundMetric(totalVolume / pourCount) : 0,
    lastPourDate,
    days,
    mixDesigns: Array.from(mixDesigns).sort((a, b) => a.localeCompare(b)),
  };
}

/**
 * Resolve the Worker Hours Type label for metric prefixes.
 *
 * @param {string} typeKey - ``all`` or WorkerType id.
 * @param {Array<{id: number, type: string}>|null|undefined} workerTypes
 * @returns {string}
 */
export function getWorkerHoursTypeLabel(typeKey, workerTypes) {
  if (!typeKey || typeKey === OVERVIEW_METRICS_TYPE_ALL) {
    return "All";
  }
  const match = (workerTypes || []).find(
    (workerType) => String(workerType.id) === String(typeKey)
  );
  return match?.type || "All";
}

/**
 * Resolve the Concrete Pours Type label for metric prefixes.
 *
 * @param {string} typeKey - Concrete type filter key.
 * @returns {string}
 */
export function getConcretePoursTypeLabel(typeKey) {
  const match = CONCRETE_POURS_TYPE_OPTIONS.find(
    (option) => option.value === typeKey
  );
  return match?.label || "All";
}

/**
 * Prefix a label with the active Type name (metrics tiles and chart datasets).
 * When Type is All, return the base label only — the filter is implied.
 *
 * @param {string} typeLabel
 * @param {string} baseLabel
 * @returns {string}
 */
export function withTypePrefix(typeLabel, baseLabel) {
  if (!typeLabel || typeLabel === "All") {
    return baseLabel;
  }
  return `${typeLabel} ${baseLabel}`;
}

/**
 * Build the four Worker Hours overview tiles from aggregated totals.
 *
 * @param {Object|null} data - Aggregated worker-hours payload.
 * @param {string} [typeKey=OVERVIEW_METRICS_TYPE_ALL] - Active Type filter.
 * @param {Array<{id: number, type: string}>|null|undefined} [workerTypes]
 * @returns {Array<{value: string, label: string, accent?: string}>}
 */
export function buildWorkerHoursMetrics(
  data,
  typeKey = OVERVIEW_METRICS_TYPE_ALL,
  workerTypes = null
) {
  const totalHours = Number(data?.totalHours) || 0;
  const regularHours = Number(data?.regularHours) || 0;
  const overtimeHours = Number(data?.overtimeHours) || 0;
  const doubleTimeHours = Number(data?.doubleTimeHours) || 0;
  const workerCount = Number(data?.workerCount) || 0;
  const typeLabel = getWorkerHoursTypeLabel(typeKey, workerTypes);

  return [
    {
      value: formatNumber(totalHours, 1, true, true),
      label: withTypePrefix(typeLabel, "Total hours"),
    },
    {
      value: formatNumber(regularHours, 1, true, true),
      label: withTypePrefix(typeLabel, "Regular Time"),
    },
    {
      value: formatNumber(overtimeHours, 1, true, true),
      label: withTypePrefix(typeLabel, "Overtime"),
      accent: "overtime",
    },
    {
      value: formatNumber(doubleTimeHours, 1, true, true),
      label: withTypePrefix(typeLabel, "Double Time"),
      accent: "doubletime",
    },
    {
      value: formatNumber(workerCount, 0, true, true),
      label: withTypePrefix(typeLabel, "Workers"),
    },
  ];
}

/**
 * Placeholder metrics shown while payout hours are loading.
 *
 * @param {string} [typeKey=OVERVIEW_METRICS_TYPE_ALL]
 * @param {Array<{id: number, type: string}>|null|undefined} [workerTypes]
 * @returns {Array<{value: string, label: string}>}
 */
export function getLoadingWorkerHoursMetrics(
  typeKey = OVERVIEW_METRICS_TYPE_ALL,
  workerTypes = null
) {
  const typeLabel = getWorkerHoursTypeLabel(typeKey, workerTypes);
  return [
    { value: "…", label: withTypePrefix(typeLabel, "Total hours") },
    { value: "…", label: withTypePrefix(typeLabel, "Regular Time") },
    {
      value: "…",
      label: withTypePrefix(typeLabel, "Overtime"),
      accent: "overtime",
    },
    {
      value: "…",
      label: withTypePrefix(typeLabel, "Double Time"),
      accent: "doubletime",
    },
    { value: "…", label: withTypePrefix(typeLabel, "Workers") },
  ];
}

/**
 * Build the four Concrete Pours overview tiles from aggregated totals.
 *
 * @param {Object|null} data - Aggregated concrete-pours payload.
 * @param {string} [typeKey=OVERVIEW_METRICS_TYPE_ALL] - Active Type filter.
 * @returns {Array<{value: string, label: string}>}
 */
export function buildConcretePoursMetrics(
  data,
  typeKey = OVERVIEW_METRICS_TYPE_ALL
) {
  const totalVolume = Number(data?.totalVolume) || 0;
  const pourCount = Number(data?.pourCount) || 0;
  const averagePerPour = Number(data?.averagePerPour) || 0;
  const lastPourDate = data?.lastPourDate
    ? formatShortMonthDay(getDateInLocalTZFromDateStr(data.lastPourDate))
    : "—";
  const typeLabel = getConcretePoursTypeLabel(typeKey);

  return [
    {
      value: `${formatNumber(totalVolume, 1, true, true)} CY`,
      label: withTypePrefix(typeLabel, "Total volume"),
    },
    {
      value: formatNumber(pourCount, 0, true, true),
      label: withTypePrefix(typeLabel, "Pours"),
    },
    {
      value: `${formatNumber(averagePerPour, 1, true, true)} CY`,
      label: withTypePrefix(typeLabel, "Avg / pour"),
    },
    {
      value: lastPourDate,
      label: withTypePrefix(typeLabel, "Last pour"),
    },
  ];
}

/**
 * Placeholder metrics shown while concrete pours are loading.
 *
 * @param {string} [typeKey=OVERVIEW_METRICS_TYPE_ALL]
 * @returns {Array<{value: string, label: string}>}
 */
export function getLoadingConcretePoursMetrics(
  typeKey = OVERVIEW_METRICS_TYPE_ALL
) {
  const typeLabel = getConcretePoursTypeLabel(typeKey);
  return [
    { value: "…", label: withTypePrefix(typeLabel, "Total volume") },
    { value: "…", label: withTypePrefix(typeLabel, "Pours") },
    { value: "…", label: withTypePrefix(typeLabel, "Avg / pour") },
    { value: "…", label: withTypePrefix(typeLabel, "Last pour") },
  ];
}
